import { UserId } from '@business/entities/account';
import { PageQuery, Paginate } from '@business/entities/common';
import { JobId } from '@business/entities/job';
import {
  ACHMethodRequest,
  ManualSupportHoursRequest,
  OrgAddressResponse,
  OrgId,
  OrgRequest,
  OrgResponse,
  PaymentId,
  PaymentMethodRequest,
  PaymentMethodResponse,
} from '@business/entities/organization';
import { ChangeSubscriptionResponse, ConvertProjectRequest } from '@business/entities/project';
import { SubscriptionId, SubscriptionResponse } from '@business/entities/project/SubscriptionResponse';
import { InvoiceId } from '@business/entities/TransactionInvoice';
import { CommonService, CustomFieldService } from '@business/services';
import { R } from '@core/utils/r';
import { SubscriptionRequest } from '@modules/subscription/CreateSubscriptionPage/SubscriptionForm';
import { Endpoint } from '../endpoint';
import {
  CanCreateResponse,
  CompactOrgResponse,
  OrgBillingType,
  OrgResponseAdditional,
  PublicOrgResponse,
  RefundFormResponse,
  RefundLineResponse,
} from './../../entities/organization/OrgResponse';
import { VendastaAccountResponse } from './../../entities/organization/VendastaAccountResponse';
import { ContactId } from '@business/entities/contact';

export function fetchOrganizations(params: PageQuery) {
  return Endpoint.get<Paginate<OrgResponse, OrgResponseAdditional>>(`@gc/organizations`, {
    params: R.objectCompact(params, { number: true, string: true }),
  });
}

export function fetchOrgs() {
  return Endpoint.get<CompactOrgResponse[]>(`@gc/organizations/all`);
}

export function escalateOrgs() {
  return Endpoint.get<CompactOrgResponse[]>(`@gc/organizations`, { params: { isEscalate: 1 } });
}

export function fetchOrganization(id: OrgId) {
  return Endpoint.get<OrgResponse>(`@gc/organizations/organization/${id}`);
}

export function fetchOrganizationDetails(id: OrgId, infoOnly = false) {
  return Endpoint.get<OrgResponse>(`@gc/organizations/organization-details/${id}`, { params: { infoOnly } });
}

export function fetchCompactOrganization(id: OrgId) {
  return Endpoint.get<OrgResponse>(`@gc/organizations/compact-organization/${id}`);
}

export function fetchClientOrgs() {
  return Endpoint.get<OrgResponse[]>(`@gc/organizations/client-orgs`);
}

export function canCreate(org: OrgRequest, orgId?: OrgId) {
  return Endpoint.post<CanCreateResponse>(`@gc/organizations/can-create/${orgId ? orgId : 0}`, { data: { ...org } });
}

export function createOrganization(org: OrgRequest) {
  if (CustomFieldService.hasFile(org.customFields)) {
    const formData = CommonService.toFormData(org);
    return Endpoint.post<OrgResponse>(`@gc/organizations/create`, { formData, hasFiles: true });
  }

  return Endpoint.post<OrgResponse>(`@gc/organizations/create`, { data: org });
}

export function updateOrganization(id: OrgId, org: OrgRequest, changeOwnership = false) {
  if (CustomFieldService.hasFile(org.customFields)) {
    const formData = CommonService.toFormData({ ...org, changeOwnership });

    return Endpoint.post<OrgResponse>(`@gc/organizations/update/${id}`, { formData, hasFiles: true });
  }
  return Endpoint.post<OrgResponse>(`@gc/organizations/update/${id}`, { data: { ...org, changeOwnership } });
}

export function addSubAccount(id: OrgId, childId: OrgId, billingType: OrgBillingType) {
  return Endpoint.post<OrgResponse>(`@gc/organizations/child/${id}`, { data: { childId, billingType } });
}

export function deleteSubAccount(id: OrgId, childId: OrgId) {
  return Endpoint.delete<OrgResponse>(`@gc/organizations/child/${id}`, { data: { childId } });
}

export function updateWebsites(id: OrgId, websites: string[]) {
  return Endpoint.patch<OrgResponse>(`@gc/organizations/websites/${id}`, { data: { websites: R.compact(websites) } });
}

export function syncOrganization(id: OrgId) {
  return Endpoint.post<OrgResponse>(`@gc/organizations/sync/${id}`);
}
export function syncPayments(id: OrgId) {
  return Endpoint.post<OrgResponse>(`@gc/organizations/sync-trans/${id}`);
}

export function deleteOrganization(orgId: OrgId) {
  return Endpoint.delete<void>(`@gc/organizations/${orgId}`);
}

/** Addresses */

export function createAddress(orgId: OrgId, data: Partial<OrgAddressResponse>) {
  return Endpoint.post<OrgAddressResponse>(`@gc/organizations/address-create/${orgId}`, { data });
}

export function updateAddress(id: string, data: Partial<OrgAddressResponse>) {
  return Endpoint.patch<OrgAddressResponse>(`@gc/organizations/address/${id}`, { data });
}

export function deleteAddress(id: string) {
  return Endpoint.delete<void>(`@gc/organizations/address/${id}`);
}

export function fetchAddresses(orgId: OrgId, ids?: string[]) {
  return Endpoint.get<OrgAddressResponse[]>(`@gc/organizations/addresses/${orgId}`, { params: { ids } });
}

export function makeAddressPrimary(id: string) {
  return Endpoint.post<OrgAddressResponse>(`@gc/organizations/address-default/${id}`);
}

/** Payments */

export function createPaymentMethod(orgId: OrgId, data: PaymentMethodRequest) {
  return Endpoint.post<PaymentMethodResponse>(`@gc/payment-method/create/${orgId}`, { data });
}
export function createAchMethod(orgId: OrgId, data: ACHMethodRequest) {
  return Endpoint.post<PaymentMethodResponse>(`@gc/payment-method/ach-create/${orgId}`, { data });
}

// export function updatePaymentMethod(id: string, data: PaymentMethodRequest) {
//   return Endpoint.patch<PaymentMethodRequest>(`@gc/address/${id}`, { data });
// }

export function makeDefaultPaymentMethod(id: string) {
  return Endpoint.post<OrgResponse>(`@gc/payment-method/make-default/${id}`);
}

export function updateSubPaymentMethod(id: PaymentId) {
  return Endpoint.post<OrgResponse>(`@gc/payment-method/update-sub-payment/${id}`);
}

export function deletePaymentMethod(id: PaymentId) {
  return Endpoint.delete<void>(`@gc/payment-method/${id}`);
}

export function createSubscription(id: OrgId, data: SubscriptionRequest) {
  const formData = CommonService.toFormData(data);
  return Endpoint.post<void>(`@gc/organizations/subscription/${id}`, { formData, hasFiles: true });
}

export function updateSubscription(id: SubscriptionId, data: SubscriptionRequest) {
  const formData = CommonService.toFormData(data);
  return Endpoint.post<void>(`@gc/organizations/subscription/update/${id}`, { formData, hasFiles: true });
}

export function createSubInvoice(id: SubscriptionId, data: SubscriptionRequest) {
  const formData = CommonService.toFormData(data);
  return Endpoint.post<void>(`@gc/organizations/subscription/invoice/${id}`, { formData, hasFiles: true });
}

export function resendLastInvoice(id: SubscriptionId) {
  return Endpoint.post<void>(`@gc/organizations/subscription/resend-invoice/${id}`);
}

export function cancelSubscription(id: SubscriptionId, reason: string) {
  return Endpoint.post<SubscriptionResponse[]>(`@gc/organizations/subscription/cancel/${id}`, { data: { reason } });
}

export function syncSubscription(id: SubscriptionId) {
  return Endpoint.post<SubscriptionResponse[]>(`@gc/organizations/subscription/sync/${id}`, { data: {} });
}

export function manualSupportHours(orgId: OrgId, data: ManualSupportHoursRequest) {
  return Endpoint.post<void>(`@gc/organizations/manual-support-hours/${orgId}`, { data });
}

export function convertProject(orgId: OrgId, data: ConvertProjectRequest) {
  return Endpoint.post<OrgResponse>(`@gc/organizations/convert-project/${orgId}`, { data });
}

export function changePaymentMethod(subscriptionId: string, paymentId: PaymentId) {
  return Endpoint.patch<OrgResponse>(`@gc/organizations/subscription/payment-method/${subscriptionId}`, { data: { paymentId } });
}

export function changeOwner(userId: UserId, orgIds: OrgId[], changeOwnership = false) {
  return Endpoint.post<void>('@gc/organizations/change-owner', { data: { userId, orgIds, changeOwnership } });
}

export function deleteMultiple(orgIds: OrgId[]) {
  return Endpoint.post<void>('@gc/organizations/delete-multiple', { data: { orgIds } });
}

export function merge(orgIds: OrgId[], primary: OrgId) {
  return Endpoint.post<void>('@gc/organizations/merge', { data: { orgIds, primary } });
}

export function getVendastaAccounts(search: string) {
  return Endpoint.get<VendastaAccountResponse[]>(`@gc/organizations/vendasta-accounts`, { params: { search } });
}

export function setVendastaAccounts(orgId: OrgId, id: string, name: string) {
  return Endpoint.post<CompactOrgResponse>(`@gc/organizations/vendasta-account/${orgId}`, { data: { id, name } });
}

export function sendInvoice(invoiceId: InvoiceId) {
  return Endpoint.post<void>(`@gc/organizations/send-invoice/${invoiceId}`);
}

export function voidInvoice(invoiceId: InvoiceId) {
  return Endpoint.post<OrgResponse>(`@gc/organizations/void-invoice/${invoiceId}`);
}

export function processPayment(invoiceId: InvoiceId, methodId: PaymentId, amount?: number) {
  return Endpoint.post<OrgResponse>(`@gc/organizations/process-invoice/${invoiceId}`, { data: R.objectCompact({ methodId, amount }) });
}

export function exportOrganizations(data: PageQuery) {
  return Endpoint.post<void>('@gc/organizations/export', { data });
}

export function downloadExport(jobId: JobId, filename: string) {
  return Endpoint.get<void>(`@gc/organizations/download-export/${jobId}`, { isFile: true, filename });
}

export function getPublicOrganization(orgId: OrgId, withAddress = false) {
  if (withAddress) {
    return Endpoint.get<PublicOrgResponse>(`@gc/public/organization/${orgId}`, { params: { withAddress } });
  }
  return Endpoint.get<PublicOrgResponse>(`@gc/public/organization/${orgId}`);
}

export function publicAddACH(orgId: OrgId, data: ACHMethodRequest) {
  return Endpoint.post<void>(`@gc/public/organization/ach/${orgId}`, { data });
}

export function publicAddCard(orgId: OrgId, data: PaymentMethodRequest) {
  return Endpoint.post<void>(`@gc/public/organization/card/${orgId}`, { data });
}

export function getPaybiltSdkToken(orgId: OrgId) {
  return Endpoint.post<{ success: boolean; token: string; baseUrl: string; sdkScriptUrl: string; message?: string, testMode?: boolean }>(
    `@gc/public/organization/${orgId}/paybilt-sdk-token`,
    { data: {} },
  );
}

export function mergeContacts(orgId: OrgId, contacts: ContactId[], primary: ContactId) {
  return Endpoint.post<OrgResponse>(`@gc/organizations/merge-contacts/${orgId}`, { data: { contacts, primary } });
}

export function showOnBoard(id: string) {
  return Endpoint.post<OrgResponse>(`@gc/organizations/project/show-on-board/${id}`);
}

export function downloadInvoice(id: string, filename: string) {
  return Endpoint.get<void>(`@gc/organizations/project/download-invoice/${id}`, { isFile: true, filename });
}

export function syncInvoice(id: string) {
  return Endpoint.get<void>(`@gc/organizations/sync-invoice/${id}`);
}

export function getTags(orgId?: OrgId, contactId?: ContactId) {
  return Endpoint.get<{ orgTags: string[]; contactTags: string[]; organizationName: string; contactName: string }>(`@gc/organizations/tags`, {
    params: R.objectCompact({ orgId, contactId }),
  });
}

export function fetchChangeSubscriptions(subId: SubscriptionId) {
  return Endpoint.get<ChangeSubscriptionResponse[]>('@gc/organizations/change-subscriptions/' + subId);
}

export function updateAffiliates(orgId: string, affiliates: { userId: UserId; percentage?: number }[]) {
  return Endpoint.post<void>(`@gc/organizations/affiliates`, { data: { orgId, affiliates } });
}

export function getAffiliates(orgId: string) {
  return Endpoint.get<{ userId: string; percentage: number }[]>(`@gc/organizations/affiliates/${orgId}`);
}

export function refundPayment(transactionId: number, data: RefundFormResponse) {
  return Endpoint.post<OrgResponse>(`@gc/organizations/refund-payment`, { data: { transactionId, ...data } });
}

export function summary(orgId: string) {
  return Endpoint.get<OrgResponse>(`@gc/organizations/summary/${orgId}`);
}
export function onCommunication(orgId: string) {
  return Endpoint.get<{ details: string }>(`@gc/organizations/communications/${orgId}`);
}
